import React, { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogActions } from '@/components/catalyst-ui/dialog'
import { Button } from '@/components/catalyst-ui/button'
import { useActiveWorkspace } from '@/state/selection.store'
import { useSearchParams } from 'react-router-dom'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import ProgressSteps from './ProgressSteps'
import PeriodStep from './PeriodStep'
import ContactInfoStep from './ContactInfoStep'
import PrivacyStep from './PrivacyStep'
import ReviewAndPayStep from './ReviewAndPayStep'
import { calculatePeriodPrice, getPerYearPrice } from '../utils/priceCalculations'
import { safeLocalStorage } from '@/lib/safe-storage'

/** ---------------- Main Modal ---------------- **/
export default function MultiStepDomainRegistrationModal({ open, onClose, domainData, projectId = null }) {
  const [searchParams] = useSearchParams()
  const { workspaceId: storeWorkspaceId } = useActiveWorkspace()
  const { user } = useAuth()
  // Fallback to store if URL param is not present
  const workspaceId = searchParams.get('workspace') || storeWorkspaceId

  console.log('=== MODAL DEBUG START ===');
  console.log('URL workspace param:', searchParams.get('workspace'));
  console.log('Store workspaceId:', storeWorkspaceId);
  console.log('Final workspaceId:', workspaceId);
  console.log('=== MODAL DEBUG END ===');
  const [currentStep, setCurrentStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    domain: '',
    period: 1,
    auto_renew: true,
    whois_privacy: true,
    useSameContact: true,
    useSameContactAdmin: true,
    nameservers: ['ns1.systemdns.com', 'ns2.systemdns.com'],

    // Contact
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    org_name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    fax: '',
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (domainData && open) {
      const domain = domainData?.domain || domainData?.name || domainData?.domainName || ''
      setFormData(prev => ({ ...prev, domain }))
    }
  }, [domainData, open])

  useEffect(() => {
    if (open && user?.email) {
      setFormData(prev => ({ ...prev, email: user.email }))
    }
  }, [open, user?.email])

  if (!open) return null

  const domain = domainData?.domain || domainData?.name || domainData?.domainName || ''
  const basePrice = parseFloat(domainData.price) || 0

  const steps = [
    { key: 'period', title: 'Period' },
    { key: 'contact', title: 'Contact Info' },
    { key: 'privacy', title: 'Privacy' },
    { key: 'checkout', title: 'Checkout' },
  ]

  const validateStep = (step) => {
    const newErrors = {}

    if (step === 2) {
      const required = ['first_name', 'last_name', 'email', 'phone', 'address1', 'city', 'state', 'postal_code']
      required.forEach((f) => { if (!formData[f]?.trim()) newErrors[f] = 'This field is required' })
      if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email'
      if (formData.phone?.trim()) {
        const phone = formData.phone.trim()
        if (!phone.startsWith('+')) {
          newErrors.phone = 'Phone number must include country code'
        } else {
          try {
            const parsed = parsePhoneNumber(phone)
            if (parsed && parsed.country) {
              const isValid = isValidPhoneNumber(phone, parsed.country) && parsed.isValid()
              if (!isValid) {
                newErrors.phone = 'Please enter a valid phone number'
              }
            } else {
              if (!isValidPhoneNumber(phone)) {
                newErrors.phone = 'Please enter a valid phone number'
              }
            }
          } catch (e) {
            newErrors.phone = 'Please enter a valid phone number'
          }
        }
      } else {
        newErrors.phone = 'This field is required'
      }
      if (formData.postal_code?.trim()) {
        const postal = formData.postal_code.trim()
        const country = formData.country
        if (country === 'US' && !/^\d{5}(-\d{4})?$/.test(postal)) newErrors.postal_code = 'US ZIP must be 12345 or 12345-6789'
        if (country === 'CA' && !/^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/.test(postal)) newErrors.postal_code = 'CA postal must be A1A 1A1'
        if (country === 'GB' && !/^[A-Za-z]{1,2}\d[A-Za-z\d]?\s?\d[A-Za-z]{2}$/.test(postal)) newErrors.postal_code = 'Enter a valid UK postcode'
        if (country === 'AU' && !/^\d{4}$/.test(postal)) newErrors.postal_code = 'AU postcode must be 4 digits'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validateStep(currentStep)) setCurrentStep(prev => Math.min(prev + 1, steps.length))
  }
  const handlePrevious = () => setCurrentStep(prev => Math.max(prev - 1, 1))

  // Stripe Checkout redirect (server creates session, webhook registers domain)
  // Replace the handleStripeCheckout function (around line 320):

  async function handleStripeCheckout() {
    console.log('============================================');
    console.log('HANDLE STRIPE CHECKOUT CALLED');
    console.log('============================================');

    try {
      setIsLoading(true)

      console.log('🔍 Debug - workspaceId:', workspaceId);
      console.log('🔍 Debug - storeWorkspaceId:', storeWorkspaceId);
      console.log('🔍 Debug - URL param:', searchParams.get('workspace'));
      console.log('🔍 Debug - typeof workspaceId:', typeof workspaceId);
      console.log('🔍 Debug - workspaceId is falsy?', !workspaceId);

      if (!workspaceId) {
        console.error('❌ workspaceId is missing!');
        alert('Workspace ID is missing. Please try logging out and logging back in.');
        throw new Error('workspaceId missing - please select a workspace');
      }
      if (!formData?.domain || !formData.domain.trim()) throw new Error('Domain is required');

      // Calculate total price for the selected period, including privacy if selected
      const years = Number(formData.period);
      const currency = domainData?.currency || 'usd';

      const basePrice = Number(domainData?.price || 0);
      const prices = domainData?.prices || {};
      const periodPrice = calculatePeriodPrice(years, basePrice, prices);

      // Per-year price in dollars (2 decimals); backend will do *100 and multiply by years
      const pricePerYear = Number(getPerYearPrice(periodPrice, years).toFixed(2));

      const returnUrl = `${window.location.origin}/domains/my-domains?workspace=${workspaceId}`
      
      const payload = {
        workspaceId,
        returnUrl,
        domainData: {
          ...formData,
          domain: formData.domain.trim(),
          price: pricePerYear,   // per-year dollars, e.g. 10.85
          year: years,           // NOTE: singular 'year' to match backend
          currency,              // e.g. 'usd'
        },
        ...(projectId ? { projectId } : {}), // Include projectId if provided
      };

      console.log('📦 Payload being sent:', JSON.stringify(payload, null, 2));

      // Use the development API for domain payments
      const base = import.meta.env.VITE_API_BASE_URL;
      const token = safeLocalStorage.getItem('auth_token');



      const apiUrl = `${base}payments/domain/purchase`;
      console.log('📍 Full API URL:', apiUrl);

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'x-access-token': token } : {}),
        },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        console.error('❌ API Error Response:', {
          status: res.status,
          statusText: res.statusText,
          url: res.url
        });
        const errorData = await res.json().catch(() => ({ error: 'Checkout error' }))
        throw new Error(errorData.error || errorData.message || 'Failed to start checkout')
      }

      const responseData = await res.json()
      const { checkoutUrl } = responseData.data
      if (checkoutUrl) window.location.href = checkoutUrl
      else throw new Error('No checkout URL received from server')
    } catch (e) {
      alert(e.message || 'Unable to start checkout')
    } finally {
      setIsLoading(false)
    }
  }

  const renderStep = () => {
    const currentDomain = formData.domain || domain
    switch (currentStep) {
      case 1:
        return <PeriodStep formData={formData} setFormData={setFormData} domain={currentDomain} domainPriceData={domainData} />
      case 2:
        return <ContactInfoStep formData={formData} setFormData={setFormData} errors={errors} domain={currentDomain} />
      case 3:
        return <PrivacyStep formData={formData} setFormData={setFormData} domainPriceData={domainData} domain={currentDomain} />
      case 4:
        return <ReviewAndPayStep formData={formData} setFormData={setFormData} domainPriceData={domainData} domain={currentDomain} />
      default:
        return null
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="3xl"
      className="!rounded-xl !p-5"
    >
      <DialogBody className="!flex flex-col items-center justify-center overflow-y-visible relative">
        <button
          onClick={onClose}
          disabled={isLoading}
          className="absolute -top-7 -right-2 text-black hover:text-zinc-600 disabled:opacity-50 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 rounded-full z-10"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
        <ProgressSteps currentStep={currentStep} steps={steps} />
        <div className="mt-4 pb-8">{renderStep()}</div>
      </DialogBody>

      <DialogActions className="flex-shrink-0 !mt-[-16px]">
        <div className="flex gap-3 items-center justify-end">
          <Button
            outline
            onClick={currentStep === 1 ? onClose : handlePrevious}
            disabled={isLoading} 
            className="data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2">
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </Button>

          <Button
            onClick={currentStep === steps.length ? handleStripeCheckout : handleNext}
            className="hover:opacity-90 bg-[#5C33FF] text-white data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
            disabled={isLoading}
            color
          >
            {isLoading ? 'Processing...' : currentStep === steps.length ? 'Pay with Stripe' : 'Continue'}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  )
}
